import * as FlipdishApi from '@flipdish/api-client-typescript';
import { localeReducer as locale } from 'react-localize-redux';
import { reducer as permissions } from 'react-redux-permissions';
import * as Redux from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import createSagaMiddleware from 'redux-saga';
import thunk from 'redux-thunk';
import * as BuildConstants from '../BuildConstants';
import * as Api from './Api';
import * as Basket from './BasketControl';
import * as Data from './Data';
import * as OrderFlow from './OrderFlow';
import { initSaga, reducers as newAgeReducers, defaultState } from './redux';
import * as Settings from './SettingsStore';
import * as Utilities from './Utilities';
import * as Vouchers from './Vouchers';

const _ = require('lodash');

export enum LocationCertainty {
  Hardcoded = 1,
  AppConfig,
  GeoIp,
  Settings,
  GeoLocation,
  User
}

export interface LocationGuess {
  certainty: LocationCertainty;
  geo: Api.Geo;
}

export interface MenuData {
  // TODO - remove the version from the key here
  // We can index purely by VRID
  // When we load we can invalidate and over-write.
  // We need to store basket by menu version though if we choose to
  // persist baskets in redux.
  [MenuKey: string]: Data.Async<Api.MenuReposnse> | undefined;
}

export interface Baskets {
  tip?: number;

  // TODO:DN
  [menuId: number]: Basket.Data | undefined;
}

export interface ExpandedStates {
  [vrid: number]: number[];
}

export interface CardOrder {
  vrid: number;
  prid: number;
  chefNote: string;
  deliveryTime: string;
  type: 'collection' | 'delivery';
  id: number;
  currency: Api.Currency;
  total: number;
}

//For storing Chef Notes
export interface ChefNotes {
  // TODO:DN
  [menuId: number]: string | undefined;
}

//For storing Table Numbers
export interface TableNumbers {
  [vrid: number]: string | undefined;
}

//For storing Pickup Location Type
export interface PickupLocationTypes {
  [vrid: number]: Api.PickupLocationType | undefined;
}

//For storing Tax Information
export interface TaxRateIds {
  [elementId: number]: number | undefined;
}

export interface AsyncMap<K> {
  [key: string]: Data.Async<K> | undefined;
}

export type CollectionMap = AsyncMap<Api.CollectionRestaurantDetail>;
export type DeliveryMap = AsyncMap<Api.DeliveryRestaurantDetail>;

export const DeliveryKey = (vrid: number | string, dlid: number | string): string => {
  const key = 'del-' + vrid + '-' + dlid;
  return key;
};

export type DeliveryOption =
  | { type: 'skip'; to: Api.DeliveryRestaurantSummary }
  | { type: 'choice'; of: Api.DeliveryRestaurantSummary[] };

export interface FlipdishState {
  DeliveryLocations: Data.Async<Api.DeliveryLocation[]>;
  CollectionSummaries: Data.Async<Api.CollectionRestaurantSummary[]>;
  DeliverySummaries: Data.Async<Api.DeliveryRestaurantSummary[]>;
  Config: Data.Config;
  MenuData: MenuData;
  ToastMessages: string[];
  AppConfig: Data.Async<Api.AppConfig>;
  Customer: Data.Async<Api.Customer & { TipPercentageOptions: number[] }>;
  // Where to navigate to after login.
  AfterLogin: string;
  LastRestaurantUrl: string;
  CountryCode: string;
  Location: LocationGuess;
  Baskets: Baskets;
  ChefNotes: ChefNotes;
  TableNumbers: TableNumbers;
  PickupLocationTypes: PickupLocationTypes;
  Order: OrderFlow.State;
  MenuScrollY: number;
  CanCancelOrder: boolean;
  PhoneNumberConfig: Api.PhoneNumberConfig;
  addressSuggestion: { suggestion: string; postalCode: any };
  Bounced: boolean;
  OptionsState: {
    Options: Api.MenuItemOptionSetItem[];
    //  OptionsBeforeNextScreen: Array<Api.MenuItemOptionSetItem>,
    PrevScreenOptions: Api.MenuItemOptionSetItem[][];
    Current: number;
    PrevIndices: number[];
  } | null;
  isDark: boolean;
  Offset: number;
  ExpandedStates: ExpandedStates;
  ClientAnalytics: boolean;
  CardOrder: CardOrder | null;
  EditMode: boolean;
  Roles: string[];
  Editing: boolean;
  NewSectionCreating: boolean;
  NewItemCreating: number;
  VouchersInProgress: number;
  HideHidden: boolean;
  ItemTaxes: TaxRateIds;
  OptionItemTaxes: TaxRateIds;
}

export interface IntercomSettings {
  app_id: string | null;
  user_id: string | null;
  email: string | null;
  name: string | null;
  whitelabelid: number | null;
  flipdishuserid: number | null;
  hide_default_launcher: true;
  promotions_enabled: boolean | null;
  phone: string | null;
}

const EmptyIntercom: IntercomSettings = {
  app_id: null,
  user_id: null,
  email: null,
  name: null,
  whitelabelid: null,
  flipdishuserid: null,
  promotions_enabled: null,
  hide_default_launcher: true,
  phone: null
};

export interface Language {
  code: string;
  active: boolean;
}

export interface LocaleOptions {
  renderInnerHtml: boolean;
  showMissingTranslationMsg: boolean;
  defaultLanguage?: string;
}

export interface LocaleSettings {
  languages: Language[];
  translations: object;
  options: LocaleOptions;
}

const EmptyLocale: LocaleSettings = {
  languages: [],
  translations: {},
  options: {
    renderInnerHtml: true,
    showMissingTranslationMsg: true
  }
};

const DefaultAppDesignScheme: Api.AppDesignScheme = {
  Primary: BuildConstants.PRIMARY_COLOR,
  Secondary: BuildConstants.THEME_COLOR,
  Tertiary: BuildConstants.TERTIARY_COLOR
};

//#region GLOBAL APP TYPES
declare global {
  namespace WebOrder {
    interface State {
      flipdish: FlipdishState;
      collectionRestaurants: CollectionMap;
      deliveryRestaurants: DeliveryMap;
      deliveryOptions: AsyncMap<DeliveryOption>; // keyed by delivery location id
      intercom: IntercomSettings;
      locale: any;
      permissions: FlipdishApi.App.AppResourceSetEnum[];
      appDesignScheme: Api.AppDesignScheme;

      payments: Readonly<State.Payments>;
      basket: Readonly<State.Basket>;
      location: Readonly<State.Location>;
    }
    namespace State {
      interface Location {
        pathname: string;
        params?: {
          bounceurl?: string;
          vrid?: string;
          id?: string;
          prid?: string;
          dlid?: string;
          type?: 'collection' | 'delivery';
          backurl?: string;
          phone?: string;
          orderId?: number | string;
          concessionStoreId?: number | string;
          lat?: number | string;
          lng?: number | string;
          location?: number | string;
          item?: number | string;
          index?: number | string;
          paymentAccount?: number | string;
        };
        search?: string;
      }

      interface Payment extends Api.PaymentAccount {
        paymentId: string;
        displayOrder: number;
        status: 'none' | 'pending' | 'success' | 'error';
      }
      interface Payments {
        status: 'none' | 'pending' | 'success';
        items: {
          [paymentId: string]: Payment;
        };
        error?: Error;
      }

      //#region BASKET
      type BasketStatus =
        | 'none'
        | 'loading'
        | 'loaded'
        | 'validating'
        | 'submitting'
        | 'submitted'
        | 'error'
        | 'cancelling';
      type BasketItem = {
        itemId: number;
        options: number[];
        order: number;
        count: number;
        hasUpdate?: 'deleted' | 'price_change' | 'not_available';
      };
      type BasketCreatedOrder = Api.CreatedOrder;
      type BasketSubmittedOrder = Api.SubmitOrderResponseDm;

      type BasketValidationError = 'minimum_amount' | 'table_number' | 'requested_time' | 'name';
      type Basket = {
        status: BasketStatus;

        menuId?: number;
        menuGuid?: string;

        items: { [uniqueKey: string]: BasketItem };
        paymentId?: string;
        tip?: number;
        requestedTime?: string;
        chefNote?: string;
        pickupLocation?: Api.PickupLocationType;
        tableNumber?: number;
        over18Confirmed?: boolean;

        validationErrors: BasketValidationError[];

        /** used for creating order and voucher */
        orderLoading?: boolean;
        /** currently applied voucher */
        voucher?: {
          code: string;
          description?: string;
          amount: number;
          isUserInput?: boolean;
        };
        // [PERF] reusing orders
        createdOrders: {
          [uniqueKey: string]: BasketCreatedOrder;
        };
        submittedOrder?: BasketSubmittedOrder;

        error?: Error;
      };
      //#endregion
    }
  }
}
//#endregion
export interface State extends WebOrder.State {}

const Empty = (config: Data.Config): FlipdishState => {
  const menuId = Utilities.extraOption('data-menuid');

  return {
    DeliveryLocations: {
      status: 'pending'
    },
    CollectionSummaries: {
      status: 'pending'
    },
    DeliverySummaries: {
      status: 'pending'
    },
    Config: config,
    MenuData: {},
    ToastMessages: [],
    AppConfig: {
      status: 'pending'
    },
    Customer: {
      status: 'pending'
    },
    AfterLogin: '',
    LastRestaurantUrl: '/',
    CountryCode: '',
    Location: {
      certainty: LocationCertainty.Hardcoded,
      geo: {
        Latitude: 53.3454362,
        Longitude: -6.2616217
      }
    },
    addressSuggestion: { suggestion: '', postalCode: '' },
    Baskets: {},
    ChefNotes: {},
    TableNumbers: {},
    PickupLocationTypes: {},
    Order: {
      state: 'none',
      submitType: 'NORMAL'
    },
    MenuScrollY: 0,
    CanCancelOrder: false,
    PhoneNumberConfig: {
      // This is a valid prefix that will cause the phone number widget to be
      // 'dumb' - no auto prefix filled in, no auto-submit.
      Prefix: '',
      AutocompletePhoneNumberLength: 10000
    },
    Bounced: false,
    OptionsState: {
      Options: [],
      PrevScreenOptions: [[]],
      //  OptionsBeforeNextScreen:[],
      Current: 0,
      PrevIndices: []
    },
    isDark: false,
    Offset: 0,
    ExpandedStates: {},
    ClientAnalytics: false,
    CardOrder: null,
    EditMode: menuId ? true : false,
    Roles: [],
    Editing: false,
    NewSectionCreating: false,
    NewItemCreating: -1,
    VouchersInProgress: 0,
    HideHidden: false,
    ItemTaxes: {},
    OptionItemTaxes: {}
  };
};

export interface ClearFlipdishStore {
  type: 'CLEAR_FLIPDISH_STORE';
}

//#region Collection Restaurants----------------------------------------------------------
export interface SetRestaurantCollection {
  type: 'SET_RESTAURANT_COLLECTION';
  payload: {
    restaurant: Data.Async<Api.CollectionRestaurantDetail>;
    prid: number;
  };
}

export interface SetRestaurantCollectionPreOrderTimes {
  type: 'SET_RESTAURANT_COLLECTION_PRE_ORDER_TIMES';
  payload: {
    vrId: string;
    prId: string;
    preOrderTimes: Api.PreOrderTime[];
  };
}

const collectionReducer = (
  state: CollectionMap = {},
  action: SetRestaurantCollection | SetRestaurantCollectionPreOrderTimes
): CollectionMap => {
  switch (action.type) {
    case 'SET_RESTAURANT_COLLECTION': {
      return {
        ...state,
        [action.payload.prid]: action.payload.restaurant
      };
    }

    case 'SET_RESTAURANT_COLLECTION_PRE_ORDER_TIMES': {
      // key should be string
      const key = parseInt(action.payload.prId);

      // just in case
      if (!state[key] || state[key].status !== 'success') {
        return state;
      }

      return {
        ...state,
        [key]: {
          ...state[key],
          data: {
            // @ts-ignore 'Pending' state doesn't have data prop
            ...state[key].data,
            PreOrderOptions: {
              // @ts-ignore 'Pending' state doesn't have data prop
              ...state[key].data.PreOrderOptions,
              PreOrderTimes: action.payload.preOrderTimes
            }
          }
        }
      };
    }

    default:
      return state;
  }
};
//#endregion

//#region Delivery Restaurants----------------------------------------------------------------
export interface SetRestaurantDelivery {
  type: 'SET_RESTAURANT_DELIVERY';
  payload: {
    restaurant: Data.Async<Api.DeliveryRestaurantDetail>;
    vrid: number;
    dlid: number;
  };
}

export interface SetRestaurantDeliveryPreOrderTimes {
  type: 'SET_RESTAURANT_DELIVERY_PRE_ORDER_TIMES';
  payload: {
    vrId: string;
    dlId: string;
    preOrderTimes: Api.PreOrderTime[];
  };
}

const deliveryReducer = (
  state: DeliveryMap = {},
  action: SetRestaurantDelivery | SetRestaurantDeliveryPreOrderTimes
): DeliveryMap => {
  switch (action.type) {
    case 'SET_RESTAURANT_DELIVERY': {
      const key = DeliveryKey(action.payload.vrid, action.payload.dlid);
      return {
        ...state,
        [key]: action.payload.restaurant
      };
    }

    case 'SET_RESTAURANT_DELIVERY_PRE_ORDER_TIMES': {
      const key = DeliveryKey(action.payload.vrId, action.payload.dlId);

      // just in case
      if (!state[key] || state[key].status !== 'success') {
        return state;
      }

      return {
        ...state,
        [key]: {
          ...state[key],
          // @ts-ignore 'Pending' state doesn't have data prop
          data: {
            // @ts-ignore 'Pending' state doesn't have data prop
            ...state[key].data,
            PreOrderOptions: {
              // @ts-ignore 'Pending' state doesn't have data prop
              ...state[key].data.PreOrderOptions,
              PreOrderTimes: action.payload.preOrderTimes
            }
          }
        }
      };
    }

    default:
      return state;
  }
};
//#endregion

//#region Deferred Menus--------------------------------------------------------------------------

export interface AddToDeferredMenus {
  type: 'ADD_TO_DEFERRED_MENUS';
  payload: Api.RestaurantDetail;
}

export interface StartDeferredMenusLoading {
  type: 'START_DEFERRED_MENUS_LOADING';
}

export interface FinishDeferredMenuLoading {
  type: 'FINISH_DEFERRED_MENU_LOADING';
}

export type DeferredMenuAction =
  | AddToDeferredMenus
  | StartDeferredMenusLoading
  | FinishDeferredMenuLoading;
//#endregion

//#region all together
export interface SetAppDesignScheme {
  type: 'SET_APP_DESIGN_SCHEME';
  payload: Api.AppDesignScheme;
}

export interface IncrementVouchersInProgress {
  type: 'INCREMENT_VOUCHERS_IN_PROGRESS';
}

export interface DecrementVouchersInProgress {
  type: 'DECREMENT_VOUCHERS_IN_PROGRESS';
}

export interface SetCardOrder {
  type: 'SET_CARD_ORDER';
  payload: CardOrder | null;
}

export interface SetClientAnalytics {
  type: 'SET_CLIENT_ANALYTICS';
  payload: boolean;
}

export interface SetCanCancel {
  type: 'SET_CAN_CANCEL';
  payload: boolean;
}

export interface SetCollectionSummaries {
  type: 'SET_COLLECTION_SUMMARIES';
  payload: Data.Async<Api.CollectionRestaurantSummary[]>;
}

export interface SetDeliverySummaries {
  type: 'SET_DELIVERY_SUMMARIES';
  payload: Data.Async<Api.DeliveryRestaurantSummary[]>;
}

export interface SetOrder {
  type: 'SET_ORDER';
  payload: OrderFlow.State;
}

export interface SetBounced {
  type: 'SET_BOUNCED';
  payload: boolean;
}

export interface SetTheme {
  type: 'SET_THEME';
  payload: boolean;
}

export interface SetOffset {
  type: 'SET_OFFSET';
  payload: number;
}

export interface SetOptionsState {
  type: 'SET_OPTIONS_STATE';
  payload: {
    Options: Api.MenuItemOptionSetItem[];
    //  OptionsBeforeNextScreen: Array<Api.MenuItemOptionSetItem>,
    PrevScreenOptions: Api.MenuItemOptionSetItem[][];
    Current: number;
    PrevIndices: number[];
  };
}

export interface SetLiveOptions {
  type: 'SET_LIVE_OPTIONS';
  payload: Api.MenuItemOptionSetItem[];
}

export interface SetNextOptions {
  type: 'SET_NEXT_OPTIONS';
  payload: {
    PrevScreenOptions: Api.MenuItemOptionSetItem[][];
    Current: number;
    PrevIndices: number[];
  };
}

export interface LoadBasket {
  type: 'LOAD_BASKET';
  payload: Basket.Data;
}
export interface AddToBasket {
  type: 'ADD_TO_BASKET';
  payload: Basket.Item;
}
export interface RemoveFromBasket {
  type: 'REMOVE_FROM_BASKET';
  payload: Basket.Item;
}
export interface ClearBasket {
  type: 'CLEAR_BASKET';
  payload: Basket.Data;
}
export interface SetTip {
  type: 'BASKET_SET_TIP';
  payload: number;
}

export interface AddAddressSuggestion {
  type: 'ADD_ADDRESS_SUGGESTION';
  payload: {
    suggestion: string;
    postalCode: any;
  };
}

export interface SetVoucher {
  type: 'SET_VOUCHER';
  payload: {
    voucher: Vouchers.States;
    menuId: number;
  };
}

export interface SetRequestedTime {
  type: 'SET_REQUESTED_TIME';
  payload: {
    time: string;
    menuId: number;
  };
}

export interface InitializeExpandedState {
  type: 'INITIALIZE_EXPANDED_STATE';
  payload: {
    vrid: number;
    expandedState: number[];
  };
}

export interface AddToExpandedState {
  type: 'ADD_TO_EXPANDED_STATE';
  payload: {
    menuSectionId: number;
    vrid: number;
  };
}

export interface RemoveFromExpandedState {
  type: 'REMOVE_FROM_EXPANDED_STATE';
  payload: {
    menuSectionId: number;
    vrid: number;
  };
}

export interface EditChefNotes {
  type: 'EDIT_CHEF_NOTES';
  payload: {
    value: string;
    vrid: number;
  };
}

export interface EditPickupLocationTypes {
  type: 'EDIT_PICKUP_LOCATION_TYPES';
  payload: {
    value: Api.PickupLocationType;
    vrid: number;
  };
}

export interface EditTableNumbers {
  type: 'EDIT_TABLE_NUMBERS';
  payload: {
    value: number;
    vrid: number;
  };
}

export interface LoadPhoneNumberConfig {
  type: 'LOAD_PHONE_NUMBER_CONFIG';
  payload: Api.PhoneNumberConfig;
}

export interface LoadAppConfig {
  type: 'LOAD_APP_CONFIG';
  payload: Api.AppConfig;
}

export interface SetCountryCode {
  type: 'SET_COUNTRY_CODE';
  payload: string;
}

export interface AddLocation {
  type: 'ADD_LOCATION';
  payload: LocationGuess;
}

export interface MenuDataPanding {
  type: 'MENU_DATA_PENDING';
  menuKey: string;
}

export interface AddMenuData {
  type: 'ADD_MENU_DATA';
  menuKey: string;
  menu: Api.MenuReposnse;
}

export interface SetMenuItems {
  type: 'SET_MENU_ITEMS';
  payload: {
    menuKey: string;
    sectionIndex: number;
    menuItems: any[];
  };
}

export interface SetMenuSections {
  type: 'SET_MENU_SECTIONS';
  payload: {
    menuKey: string;
    menuSections: any[];
  };
}

export interface LoadDeliveryLocations {
  type: 'LOAD_DELIVERY_LOCATIONS';
  payload: Api.DeliveryLocation[];
}

export interface AddToast {
  type: 'ADD_TOAST';
  payload: string;
}

export interface RemoveToast {
  type: 'REMOVE_TOAST';
}

export interface PushLoginUrl {
  type: 'PUSH_LOGIN_URL';
  payload: string;
}

export interface SetRestaurantUrl {
  type: 'SET_RESTAURANT_URL';
  payload: string;
}

export interface SetCustomer {
  type: 'SET_CUSTOMER';
  payload: Data.Async<Api.Customer & { TipPercentageOptions: number[] }>;
}
export interface SetCustomerName {
  type: 'SET_CUSTOMER_NAME';
  payload: { name: string };
}

export interface SetSmsEnabled {
  type: 'SET_SMS_ENABLED';
  payload: boolean;
}

export interface SetMenuScroll {
  type: 'SET_MENU_SCROLL';
  payload: number;
}

const setMenu = (
  s: FlipdishState,
  menuKey: string,
  menu: Data.Async<Api.MenuReposnse>
): FlipdishState => {
  return {
    ...s,
    MenuData: {
      ...s.MenuData,
      [menuKey]: menu
    }
  };
};

const appDesignSchemeReducer = (
  state: Api.AppDesignScheme = DefaultAppDesignScheme,
  action: SetAppDesignScheme
): Api.AppDesignScheme => {
  switch (action.type) {
    case 'SET_APP_DESIGN_SCHEME': {
      return {
        ...state,
        ...action.payload
      };
    }
    default:
      return state;
  }
};

export interface AddDeliveryOption {
  type: 'ADD_DELIVERY_OPTION';
  payload: {
    dlid: number;
    option: DeliveryOption;
  };
}

const deliveryOptionsReducer = (
  state: AsyncMap<DeliveryOption> = {},
  action: AddDeliveryOption | LoadDeliveryLocations
) => {
  switch (action.type) {
    case 'ADD_DELIVERY_OPTION': {
      return {
        ...state,
        [action.payload.dlid]: {
          status: 'success',
          data: action.payload.option
        }
      };
    }

    case 'LOAD_DELIVERY_LOCATIONS': {
      const result = { ...state };
      action.payload.forEach((dl) => {
        if (!result[dl.DeliveryLocationId]) {
          result[dl.DeliveryLocationId] = { status: 'pending' };
        }
      });
      return result;
    }

    default:
      return state;
  }
};
//#endregion

//#region flipdishReducer
export type Action =
  | SetCardOrder
  | SetClientAnalytics
  | SetCanCancel
  | SetMenuScroll
  | SetSmsEnabled
  | SetRestaurantUrl
  | AddMenuData
  | SetMenuSections
  | SetMenuItems
  | SetCountryCode
  | AddLocation
  | MenuDataPanding
  | LoadDeliveryLocations
  | AddToast
  | RemoveToast
  | LoadAppConfig
  | PushLoginUrl
  | SetCustomer
  | SetCustomerName
  | ClearBasket
  | AddToBasket
  | LoadPhoneNumberConfig
  | RemoveFromBasket
  | SetOrder
  | LoadBasket
  | SetCollectionSummaries
  | SetDeliverySummaries
  | SetVoucher
  | SetRequestedTime
  | AddAddressSuggestion
  | EditChefNotes
  | EditTableNumbers
  | EditPickupLocationTypes
  | SetBounced
  | SetLiveOptions
  | SetNextOptions
  | SetOptionsState
  | SetTheme
  | SetOffset
  | InitializeExpandedState
  | AddToExpandedState
  | RemoveFromExpandedState
  | IncrementVouchersInProgress
  | DecrementVouchersInProgress
  | SetAppDesignScheme
  | ClearFlipdishStore
  | SetTip;

const reducer = (config: Data.Config) => {
  const defaultConfig = Empty(config);

  return (state: FlipdishState, action: Action): FlipdishState => {
    if (!state) {
      state = defaultConfig;
    }

    switch (action.type) {
      case 'CLEAR_FLIPDISH_STORE': {
        return {
          ...defaultConfig,
          OptionsState: null
        };
      }

      case 'INCREMENT_VOUCHERS_IN_PROGRESS': {
        return {
          ...state,
          VouchersInProgress: state.VouchersInProgress + 1
        };
      }

      case 'DECREMENT_VOUCHERS_IN_PROGRESS': {
        return {
          ...state,
          VouchersInProgress: state.VouchersInProgress - 1
        };
      }

      case 'SET_MENU_SECTIONS': {
        const m = state.MenuData[action.payload.menuKey];
        if (m.status != 'success') {
          return state;
        }

        let newMenuSections = [...action.payload.menuSections];

        for (let i = 0; i < newMenuSections.length; i++) {
          const menuSection = newMenuSections[i];
          const sectionId = menuSection.MenuSectionId;
          const sectionIndex = m.data.MenuSections.findIndex(
            (mSection) => mSection.MenuSectionId === sectionId
          );

          //If is a hidden/invisible menu section
          if (sectionIndex === -1) {
            //Fill in missing menu section properties
            newMenuSections = [
              ...newMenuSections.slice(0, i),
              {
                ...newMenuSections[i],
                CellAspectRatio: 0,
                CellLayoutType: 0,
                ImageName: null,
                IsDeleted: false,
                IsHiddenFromUsers: newMenuSections[i].IsHiddenFromCustomers || false,
                Menu: null,
                MenuId: 0,
                MenuSectionAvailability: {}
              },
              ...newMenuSections.slice(i + 1)
            ];

            //Fill in missing menu item properties
            for (let j = 0; j < menuSection.MenuItems.length; j++) {
              newMenuSections = [
                ...newMenuSections.slice(0, i),
                {
                  ...newMenuSections[i],
                  MenuItems: [
                    ...newMenuSections[i].MenuItems.slice(0, j),
                    {
                      ...newMenuSections[i].MenuItems[j],
                      CellAspectRatio: 0,
                      CellLayoutType: 0,
                      DailySpecialHours: [],
                      DisableVouchers: false,
                      ExcludeFromVoucherDiscounting: false,
                      ImageName: null,
                      MenuSection: null,
                      MenuSectionId: newMenuSections[i].MenuSectionId || null,
                      PriceCanIncrease: false,
                      SpicinessRating: null,
                      Tags: [],
                      TaxRateId: null
                    },
                    ...newMenuSections[i].MenuItems.slice(j + 1)
                  ]
                },
                ...newMenuSections.slice(i + 1)
              ];
            }
          } else {
            //Fill in missing menu section properties
            newMenuSections = [
              ...newMenuSections.slice(0, i),
              {
                ...newMenuSections[i],
                CellAspectRatio: m.data.MenuSections[sectionIndex].CellAspectRatio || 0,
                CellLayoutType: m.data.MenuSections[sectionIndex].CellLayoutType || 0,
                ImageName: m.data.MenuSections[sectionIndex].ImageName || null,
                IsDeleted: m.data.MenuSections[sectionIndex].IsDeleted || false,
                IsHiddenFromUsers:
                  m.data.MenuSections[sectionIndex].IsHiddenFromUsers ||
                  newMenuSections[i].IsHiddenFromCustomers ||
                  false,
                Menu: m.data.MenuSections[sectionIndex].Menu || null,
                MenuId: m.data.MenuSections[sectionIndex].MenuId || 0,
                MenuSectionAvailability:
                  m.data.MenuSections[sectionIndex].MenuSectionAvailability || {}
              },
              ...newMenuSections.slice(i + 1)
            ];

            for (let j = 0; j < menuSection.MenuItems.length; j++) {
              const itemId = menuSection.MenuItems[j].MenuItemId;

              const itemIndex = m.data.MenuSections[sectionIndex].MenuItems.findIndex(
                (mItem: any) => mItem.MenuItemId === itemId
              );

              //If is a hidden/invisible menu item
              if (itemIndex === -1) {
                //Fill in missing menu item properties
                newMenuSections = [
                  ...newMenuSections.slice(0, i),
                  {
                    ...newMenuSections[i],
                    MenuItems: [
                      ...newMenuSections[i].MenuItems.slice(0, j),
                      {
                        ...newMenuSections[i].MenuItems[j],
                        CellAspectRatio: 0,
                        CellLayoutType: 0,
                        DailySpecialHours: [],
                        DisableVouchers: false,
                        ExcludeFromVoucherDiscounting: false,
                        ImageName: null,
                        MenuSection: null,
                        MenuSectionId: newMenuSections[i].MenuSectionId || null,
                        PriceCanIncrease: false,
                        SpicinessRating: null,
                        Tags: [],
                        TaxRateId: null
                      },
                      ...newMenuSections[i].MenuItems.slice(j + 1)
                    ]
                  },
                  ...newMenuSections.slice(i + 1)
                ];
              } else {
                //Fill in missing menu item properties
                newMenuSections = [
                  ...newMenuSections.slice(0, i),
                  {
                    ...newMenuSections[i],
                    MenuItems: [
                      ...newMenuSections[i].MenuItems.slice(0, j),
                      {
                        ...newMenuSections[i].MenuItems[j],
                        CellAspectRatio:
                          m.data.MenuSections[sectionIndex].MenuItems[itemIndex].CellAspectRatio ||
                          0,
                        CellLayoutType:
                          m.data.MenuSections[sectionIndex].MenuItems[itemIndex].CellLayoutType ||
                          0,
                        DailySpecialHours:
                          m.data.MenuSections[sectionIndex].MenuItems[itemIndex]
                            .DailySpecialHours || [],
                        DisableVouchers:
                          m.data.MenuSections[sectionIndex].MenuItems[itemIndex].DisableVouchers ||
                          false,
                        ExcludeFromVoucherDiscounting:
                          m.data.MenuSections[sectionIndex].MenuItems[itemIndex]
                            .ExcludeFromVoucherDiscounting || false,
                        ImageName:
                          m.data.MenuSections[sectionIndex].MenuItems[itemIndex].ImageName || null,
                        MenuSection:
                          m.data.MenuSections[sectionIndex].MenuItems[itemIndex].MenuSection ||
                          null,
                        MenuSectionId:
                          m.data.MenuSections[sectionIndex].MenuItems[itemIndex].MenuSectionId ||
                          newMenuSections[i].MenuSectionId ||
                          null,
                        PriceCanIncrease:
                          m.data.MenuSections[sectionIndex].MenuItems[itemIndex].PriceCanIncrease ||
                          false,
                        SpicinessRating:
                          m.data.MenuSections[sectionIndex].MenuItems[itemIndex].SpicinessRating ||
                          null,
                        Tags: m.data.MenuSections[sectionIndex].MenuItems[itemIndex].Tags || [],
                        TaxRateId:
                          m.data.MenuSections[sectionIndex].MenuItems[itemIndex].TaxRateId || null
                      },
                      ...newMenuSections[i].MenuItems.slice(j + 1)
                    ]
                  },
                  ...newMenuSections.slice(i + 1)
                ];
              }
            }
          }
        }

        return {
          ...state,
          MenuData: {
            ...state.MenuData,
            [action.payload.menuKey]: {
              ...m,
              data: {
                ...m.data,
                MenuSections: newMenuSections
              }
            }
          }
        };
      }

      case 'SET_MENU_ITEMS': {
        const m = state.MenuData[action.payload.menuKey];
        if (m.status != 'success') {
          return state;
        }

        return {
          ...state,
          MenuData: {
            ...state.MenuData,
            [action.payload.menuKey]: {
              ...m,
              data: {
                ...m.data,
                MenuSections: [
                  ...m.data.MenuSections.slice(0, action.payload.sectionIndex),
                  {
                    ...m.data.MenuSections[action.payload.sectionIndex],
                    MenuItems: action.payload.menuItems
                  },
                  ...m.data.MenuSections.slice(action.payload.sectionIndex + 1)
                ]
              }
            }
          }
        };
      }

      case 'SET_CARD_ORDER': {
        return { ...state, CardOrder: action.payload };
      }

      case 'SET_CLIENT_ANALYTICS': {
        return { ...state, ClientAnalytics: action.payload };
      }

      case 'SET_CAN_CANCEL': {
        return { ...state, CanCancelOrder: action.payload };
      }

      case 'SET_MENU_SCROLL': {
        return { ...state, MenuScrollY: action.payload };
      }

      case 'SET_COLLECTION_SUMMARIES': {
        return { ...state, CollectionSummaries: action.payload };
      }

      case 'SET_DELIVERY_SUMMARIES': {
        return { ...state, DeliverySummaries: action.payload };
      }

      case 'LOAD_DELIVERY_LOCATIONS': {
        const dl: Data.Async<Api.DeliveryLocation[]> = {
          status: 'success',
          data: action.payload
        };
        return { ...state, DeliveryLocations: dl };
      }

      case 'MENU_DATA_PENDING': {
        return setMenu(state, action.menuKey, {
          status: 'pending'
        });
      }

      case 'ADD_MENU_DATA': {
        return setMenu(state, action.menuKey, {
          status: 'success',
          data: action.menu
          // data: require('../menu.spar.json')
        });
      }

      case 'LOAD_PHONE_NUMBER_CONFIG': {
        return {
          ...state,
          PhoneNumberConfig: action.payload
        };
      }

      case 'LOAD_APP_CONFIG': {
        return {
          ...state,
          PhoneNumberConfig: action.payload.PhoneNumberConfig,
          AppConfig: {
            status: 'success',
            data: action.payload
          }
        };
      }

      case 'SET_COUNTRY_CODE': {
        return {
          ...state,
          CountryCode: action.payload
        };
      }

      case 'ADD_LOCATION': {
        // Only accept updates from higher priority sources.
        if (state.Location.certainty > action.payload.certainty) {
          return state;
        }
        try {
          Settings.LocationStore.Save(action.payload.geo);
        } catch (e) {
          console.log(e.message);
        }
        return { ...state, Location: action.payload };
      }

      case 'ADD_TOAST': {
        return {
          ...state,
          ToastMessages: [...state.ToastMessages, action.payload]
        };
      }

      case 'ADD_ADDRESS_SUGGESTION': {
        console.log('payload address item', action.payload);
        return { ...state, addressSuggestion: action.payload };
      }

      case 'REMOVE_TOAST': {
        const [first, ...rest] = state.ToastMessages;
        return { ...state, ToastMessages: rest };
      }

      case 'PUSH_LOGIN_URL': {
        return { ...state, AfterLogin: action.payload };
      }

      case 'SET_RESTAURANT_URL': {
        return { ...state, LastRestaurantUrl: action.payload };
      }

      case 'SET_CUSTOMER': {
        return { ...state, Customer: action.payload };
      }
      case 'SET_CUSTOMER_NAME': {
        return {
          ...state,
          Customer: {
            ...state.Customer,
            // @ts-ignore
            data: {
              // @ts-ignore
              ...(state.Customer.data || {}),
              CustomerName: action.payload.name
            }
          }
        };
      }

      case 'SET_BOUNCED': {
        return { ...state, Bounced: action.payload };
      }

      case 'SET_THEME': {
        return { ...state, isDark: action.payload };
      }

      case 'SET_OFFSET': {
        return { ...state, Offset: action.payload };
      }

      case 'SET_LIVE_OPTIONS': {
        return {
          ...state,
          OptionsState: { ...state.OptionsState, Options: action.payload }
        };
      }

      case 'SET_NEXT_OPTIONS': {
        return {
          ...state,
          OptionsState: {
            ...state.OptionsState,
            PrevIndices: action.payload.PrevIndices,
            Current: action.payload.Current,
            PrevScreenOptions: action.payload.PrevScreenOptions
          }
        };
      }

      case 'SET_OPTIONS_STATE': {
        return { ...state, OptionsState: action.payload };
      }

      case 'SET_SMS_ENABLED': {
        const c = state.Customer;
        if (c.status != 'success') {
          return state;
        }
        return {
          ...state,
          Customer: {
            ...state.Customer,
            data: {
              ...c.data,
              SmsPromotionsEnabled: action.payload
            }
          }
        } as any;
      }

      case 'INITIALIZE_EXPANDED_STATE': {
        const { vrid, expandedState } = action.payload;
        return {
          ...state,
          ExpandedStates: {
            ...state.ExpandedStates,
            [vrid]: expandedState
          }
        };
      }

      case 'ADD_TO_EXPANDED_STATE': {
        const { menuSectionId, vrid } = action.payload;

        return {
          ...state,
          ExpandedStates: {
            ...state.ExpandedStates,
            [vrid]: state.ExpandedStates[vrid].concat([menuSectionId])
          }
        };
      }

      case 'REMOVE_FROM_EXPANDED_STATE': {
        const { menuSectionId, vrid } = action.payload;

        return {
          ...state,
          ExpandedStates: {
            ...state.ExpandedStates,
            [vrid]: Utilities.FindAndRemove(state.ExpandedStates[vrid], menuSectionId)
          }
        };
      }

      case 'SET_ORDER': {
        return { ...state, Order: action.payload };
      }
    }

    return state;
  };
};
//#endregion

//#region intercomReducer
const intercomReducer = (state: IntercomSettings = EmptyIntercom, action: Action) => {
  const withUserIdAdded = (state: IntercomSettings) => {
    if (!state.whitelabelid || !state.flipdishuserid) {
      return state;
    }
    return {
      ...state,
      user_id: state.whitelabelid + '-' + state.flipdishuserid
    };
  };

  switch (action.type) {
    case 'LOAD_APP_CONFIG': {
      return withUserIdAdded({
        ...state,
        app_id: action.payload.IntercomAppId,
        whitelabelid: action.payload.WhiteLabelId
      });
    }

    case 'SET_CUSTOMER': {
      const { payload } = action;
      if (payload.status != 'success') {
        return state;
      }
      const { data } = payload;
      return withUserIdAdded({
        ...state,
        flipdishuserid: data.UserId,
        name: data.CustomerName,
        email: data.EmailAddress,
        phone: data.PhoneNumber,
        promotions_enabled: data.SmsPromotionsEnabled
      });
    }

    default:
      return state;
  }
};
//#endregion

//#region setup & init
const sagaMiddleware = createSagaMiddleware();
const middlewares = [sagaMiddleware, thunk];

const middleware = Redux.applyMiddleware(...middlewares);

interface result {
  store: Redux.Store<State>;
}

export const watchForLocationChanges = (
  store: Redux.Store<State>,
  then: (location: Api.Geo) => any
) => {
  const sameLocation = (a: LocationGuess, b: LocationGuess): boolean => {
    return (
      a.certainty === b.certainty &&
      a.geo.Latitude === b.geo.Latitude &&
      a.geo.Longitude === b.geo.Longitude
    );
  };

  let { Location } = store.getState().flipdish;

  store.subscribe(() => {
    const NextLocation = store.getState().flipdish.Location;
    // if (process.env.NODE_ENV !== 'production') {
    //   console.log('subscription!', NextLocation, Location);
    // }
    if (!sameLocation(NextLocation, Location)) {
      // console.log('calling then!', Location, NextLocation);
      then(NextLocation.geo);
      Location = NextLocation;
    }
  });
};

let storeInstance: Redux.Store<State, Redux.Action<any>>;

export const Init = (config: Data.Config, initialState?: any): result => {
  const reducers = Redux.combineReducers<WebOrder.State>({
    flipdish: reducer(config),
    collectionRestaurants: collectionReducer,
    deliveryRestaurants: deliveryReducer,
    deliveryOptions: deliveryOptionsReducer,
    intercom: intercomReducer,
    locale,
    permissions,
    appDesignScheme: appDesignSchemeReducer,
    ...newAgeReducers
  });

  const store = Redux.createStore<State, Redux.Action<any>, {}, {}>(
    reducers,
    initialState
      ? initialState
      : {
          flipdish: Empty(config),
          collectionRestaurants: {},
          deliveryRestaurants: {},
          deliveryOptions: {},
          intercom: EmptyIntercom,
          locale: EmptyLocale,
          permissions: [],
          appDesignScheme: DefaultAppDesignScheme,
          ...defaultState
        },
    composeWithDevTools(middleware)
  );

  initSaga(sagaMiddleware);

  if (process.env.NODE_ENV === 'development') {
    (window as any)._store = store;
  }

  storeInstance = store;

  return {
    store
  };
};

export function getStoreInstance() {
  return storeInstance;
}
//#endregion
